import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { alertConfigAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Bell, Send, Shield, Clock, CheckCircle, XCircle,
  AlertTriangle, DollarSign, Truck, Calendar, Info
} from "lucide-react";

const EVENT_CONFIG = {
  nuevo_contrato: { label: "Nuevo contrato creado", icon: Bell, color: "text-blue-600" },
  pago_recibido: { label: "Pago recibido", icon: DollarSign, color: "text-green-600" },
  servicio_proximo: { label: "Servicio proximo (24h)", icon: Calendar, color: "text-amber-600" },
  sin_asignar: { label: "Sin unidad/chofer asignado", icon: Truck, color: "text-red-600" },
  comision_devengada: { label: "Comision devengada", icon: DollarSign, color: "text-purple-600" },
  contrato_cancelado: { label: "Contrato cancelado", icon: XCircle, color: "text-red-600" },
};

const COOLDOWN_INFO = {
  nuevo_contrato: "Inmediato (1 vez por contrato)",
  pago_recibido: "Inmediato (1 vez por pago)",
  servicio_proximo: "Cada 24h por contrato",
  sin_asignar: "Cada 6h por contrato",
  comision_devengada: "1 vez al cruzar umbral",
  contrato_cancelado: "Inmediato (1 vez)",
};

export default function AlertConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showGuide, setShowGuide] = useState(false);
  
  // Form fields
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [enabledEvents, setEnabledEvents] = useState(Object.keys(EVENT_CONFIG));

  const loadConfig = useCallback(async () => {
    try {
      const res = await alertConfigAPI.get();
      setConfig(res.data);
      setChatId(res.data.chat_id || "");
      setEnabled(res.data.enabled || false);
      setEnabledEvents(res.data.enabled_events || Object.keys(EVENT_CONFIG));
    } catch {
      toast.error("Error al cargar configuracion");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await alertConfigAPI.getLogs({ limit: 20 });
      setLogs(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadConfig(); loadLogs(); }, [loadConfig, loadLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await alertConfigAPI.update({
        enabled,
        bot_token: botToken || "",  // Empty = keep existing
        chat_id: chatId,
        enabled_events: enabledEvents,
      });
      toast.success("Configuracion guardada");
      setBotToken("");
      await loadConfig();
    } catch (err) {
      toast.error("Error al guardar: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await alertConfigAPI.test();
      toast.success("Alerta de prueba enviada. Revisa tu grupo de Telegram.");
      await loadLogs();
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setTesting(false);
    }
  };

  const toggleEvent = (event) => {
    setEnabledEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  if (loading) return <Layout title="Configuracion de Alertas"><div className="p-8 text-center text-slate-500">Cargando...</div></Layout>;

  return (
    <Layout title="Configuracion de Alertas">
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Alertas Telegram</h1>
            <p className="text-sm text-slate-500 mt-1">Configura notificaciones operativas en tiempo real</p>
          </div>
          <div className="flex items-center gap-3">
            {config?.bot_token_masked && config.bot_token_masked !== "***" && (
              <Badge variant="outline" className="text-green-700 bg-green-50">
                <CheckCircle className="w-3 h-3 mr-1" /> Bot configurado
              </Badge>
            )}
            {enabled ? (
              <Badge className="bg-green-100 text-green-800">Activo</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">Inactivo</Badge>
            )}
          </div>
        </div>

        {/* Connection Config */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" /> Conexion Telegram
            </CardTitle>
            <CardDescription>Token del bot y Chat ID del grupo. Los tokens se almacenan de forma segura y nunca se muestran completos.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Label className="min-w-[80px]">Habilitado</Label>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="alert-enabled-switch"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bot-token">Bot Token</Label>
                <Input
                  id="bot-token"
                  type="password"
                  placeholder={config?.bot_token_masked || "Pega el token de BotFather"}
                  value={botToken}
                  onChange={e => setBotToken(e.target.value)}
                  data-testid="bot-token-input"
                />
                <p className="text-xs text-slate-400">
                  {config?.bot_token_masked && config.bot_token_masked !== "***" 
                    ? `Token actual: ${config.bot_token_masked}. Deja vacio para conservar.`
                    : "Obtenlo de @BotFather en Telegram"}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="chat-id">Chat ID del grupo</Label>
                <Input
                  id="chat-id"
                  placeholder="-100XXXXXXXXXX"
                  value={chatId}
                  onChange={e => setChatId(e.target.value)}
                  data-testid="chat-id-input"
                />
                <p className="text-xs text-slate-400">Numero negativo del grupo (ej: -1001234567890)</p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
                {saving ? "Guardando..." : "Guardar Configuracion"}
              </Button>
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || !config?.bot_token_masked || config.bot_token_masked === "***"}
                data-testid="test-alert-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {testing ? "Enviando..." : "Enviar Alerta de Prueba"}
              </Button>
              <Button variant="ghost" onClick={() => setShowGuide(!showGuide)}>
                <Info className="w-4 h-4 mr-2" />
                {showGuide ? "Ocultar guia" : "Como configurar"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Setup Guide */}
        {showGuide && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="text-lg text-blue-800">Guia de Configuracion</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-900 space-y-4">
              <div>
                <p className="font-semibold mb-1">Paso 1: Crear el Bot</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Abre Telegram y busca <code className="bg-blue-100 px-1 rounded">@BotFather</code></li>
                  <li>Envia <code className="bg-blue-100 px-1 rounded">/newbot</code></li>
                  <li>Nombre: <code className="bg-blue-100 px-1 rounded">TenatoTour Alertas</code></li>
                  <li>Username: <code className="bg-blue-100 px-1 rounded">tenatourgdl_alertas_bot</code> (debe terminar en _bot)</li>
                  <li>BotFather te dara un token. Pegalo arriba.</li>
                </ol>
              </div>
              <div>
                <p className="font-semibold mb-1">Paso 2: Crear el Grupo</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Crea un grupo en Telegram (ej: "TenatoTour Operaciones")</li>
                  <li>Agrega al bot como miembro del grupo</li>
                  <li>Envia cualquier mensaje en el grupo (ej: "test")</li>
                </ol>
              </div>
              <div>
                <p className="font-semibold mb-1">Paso 3: Obtener Chat ID</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Abre en tu navegador: <code className="bg-blue-100 px-1 rounded text-xs">https://api.telegram.org/botTU_TOKEN/getUpdates</code></li>
                  <li>Busca <code className="bg-blue-100 px-1 rounded">"chat":{`{"id":-100XXXXXXXXXX}`}</code></li>
                  <li>Ese numero negativo es el Chat ID. Pegalo arriba.</li>
                </ol>
              </div>
              <div>
                <p className="font-semibold mb-1">Paso 4: Probar</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Guarda la configuracion</li>
                  <li>Haz clic en "Enviar Alerta de Prueba"</li>
                  <li>Deberias ver el mensaje en tu grupo de Telegram</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Events Config */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5" /> Eventos Habilitados
            </CardTitle>
            <CardDescription>Selecciona que eventos generan notificacion. Cada evento tiene control anti-spam integrado.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(EVENT_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const isOn = enabledEvents.includes(key);
                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      isOn ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-slate-50/50"
                    }`}
                    onClick={() => toggleEvent(key)}
                    data-testid={`event-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${cfg.color}`} />
                      <div>
                        <p className="font-medium text-sm">{cfg.label}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {COOLDOWN_INFO[key]}
                        </p>
                      </div>
                    </div>
                    <Switch checked={isOn} onCheckedChange={() => toggleEvent(key)} />
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-events-btn">
                {saving ? "Guardando..." : "Guardar Eventos"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notification Log */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" /> Historial de Notificaciones
            </CardTitle>
            <CardDescription>Ultimas 20 notificaciones enviadas con trazabilidad completa.</CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center text-slate-400 py-6">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Aun no hay notificaciones</p>
                <p className="text-xs">Las notificaciones apareceran aqui cuando se envien</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 text-sm">
                    <div className="mt-0.5">
                      {log.delivery_status === "sent" ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{log.event_type}</Badge>
                        <span className="text-xs text-slate-400">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString("es-MX") : "?"}
                        </span>
                      </div>
                      <p className="text-slate-600 truncate">{log.message_preview}</p>
                      {log.last_error && (
                        <p className="text-red-500 text-xs mt-1">Error: {log.last_error}</p>
                      )}
                      <div className="flex gap-3 text-xs text-slate-400 mt-1">
                        <span>Key: {log.dedupe_key}</span>
                        {log.external_message_id && <span>Msg ID: {log.external_message_id}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
